import React, { useState, useEffect, useRef } from 'react';
import Funded1 from '../../assets/about/funded1.png';
import Funded2 from '../../assets/about/funded2.png';
import Funded3 from '../../assets/about/funded3.png';

const FundedSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState({});
    const [imageErrors, setImageErrors] = useState({});
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleImageLoad = (id) => {
        setImagesLoaded(prev => ({ ...prev, [id]: true }));
    };

    const handleImageError = (id) => {
        setImageErrors(prev => ({ ...prev, [id]: true }));
    };

    const backers = [
        {
            id: 1,
            logo: Funded1,
            name: "Government of Bihar",
            description: "Official partner and supporter of RoadExpress logistics platform"
        },
        {
            id: 2,
            logo: Funded2,
            name: "Goverment of India",
            description: "Recognized by the Government of India for excellence in logistics"
        },
        {
            id: 3,
            logo: Funded3,
            name: "HDFC Bank",
            description: "Financial partner providing banking solutions"
        }
    ];

    const getLogoSize = (id) => {
        if (id === 1) return 'w-48 h-48';
        if (id === 2) return 'w-56 h-56';
        if (id === 3) return 'w-60 h-60';
        return 'w-40 h-40';
    };


    return (
        <section
            ref={sectionRef}
            className="w-full pt-8 bg-gray-50"
            aria-labelledby="backed-funded-section-title"
            role="region"
        >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Section Header */}
                <div
                    className={`text-center mb-12 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                        }`}
                >
                    <h2
                        id="backed-funded-section-title"
                        className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6"
                    >
                        Backed and Funded By
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        RoadExpress is proud to be supported by prestigious institutions and government bodies,
                        ensuring our commitment to excellence and innovation in logistics.
                    </p>
                </div>

                {/* Main Backers Display - Reduced spacing */}
                <div className="flex justify-center items-center md:gap-12">
                    {backers.map((backer, index) => (
                        <div
                            key={backer.id}
                            className={`group transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
                                }`}
                            style={{ transitionDelay: `${0.3 + index * 0.2}s` }}
                        >
                            <div className="rounded-3xl p-3 transition-all duration-500">
                                {/* Logo Container - Conditional sizing */}
                                <div className="flex justify-center">
                                    {!imageErrors[backer.id] ? (
                                        <div className="relative">
                                            {/* Loading skeleton */}
                                            {!imagesLoaded[backer.id] && (
                                                <div className={`${getLogoSize(backer.id)} rounded-2xl bg-gray-200 animate-pulse`}></div>
                                            )}
                                            <div className={`${getLogoSize(backer.id)} rounded-2xl bg-gray-50 p-6 group-hover:scale-105 transition-transform duration-300 flex items-center justify-center`}>
                                                <img
                                                    src={backer.logo}
                                                    alt={`${backer.name} - Official partner and supporter of RoadExpress logistics platform`}
                                                    className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${imagesLoaded[backer.id] ? 'opacity-100' : 'opacity-0'
                                                        }`}
                                                    onLoad={() => handleImageLoad(backer.id)}
                                                    onError={() => handleImageError(backer.id)}
                                                    loading="lazy"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`${getLogoSize(backer.id)} rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center`}>
                                            <div className="text-center">
                                                <div className="text-4xl mb-2">🏛️</div>
                                                <div className="text-sm font-medium text-gray-600">{backer.name}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Enhanced SEO Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "RoadExpress Freight",
                        "description": "Leading logistics platform backed by government institutions and prestigious technology partners",
                        "sponsor": backers.map(backer => ({
                            "@type": "Organization",
                            "name": backer.name,
                            "description": backer.description
                        })),
                        "award": [
                            "Government of India Certification",
                            "Government of Bihar Certification",
                            "ISO 27001 Compliance"
                        ],
                        "hasCredential": [
                            {
                                "@type": "EducationalOccupationalCredential",
                                "credentialCategory": "Government Certification",
                                "issuedBy": {
                                    "@type": "Organization",
                                    "name": "Government of Bihar"
                                }
                            }
                        ]
                    })
                }}
            />
        </section>
    );
};

export default FundedSection;